import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { navItems } from '@/data/navigation';
import { useEstimator } from '@/components/ProjectEstimator';
import Logo from '@/components/Logo';
import styles from './Navbar.module.scss';

export interface NavbarProps {
  theme?: 'dark' | 'light';
}

const Navbar: React.FC<NavbarProps> = ({ theme = 'dark' }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { openEstimator } = useEstimator();

  useEffect(() => {
    const handleScroll = (e: Event) => {
      let currentY = 0;
      
      // Handle both standard window scroll and Ionic custom ionScroll events
      if (e.type === 'ionScroll') {
        currentY = (e as CustomEvent).detail.scrollTop;
      } else {
        currentY = window.scrollY;
      }

      setScrolled(currentY > 20); // Small threshold as requested
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Add ionScroll listener for Ionic content areas
    window.addEventListener('ionScroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('ionScroll', handleScroll);
    };
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const navStateClass = menuOpen
    ? styles.headerMenuOpen
    : scrolled
    ? styles.headerScrolled
    : theme === 'light'
    ? styles.headerTransparentLight
    : styles.headerTransparentDark;

  return (
    <>
      <motion.header
        className={`${styles.navbar} ${navStateClass}`}
        id="navbar"
      >
        <div className={styles.inner}>
          {/* Left: Logo */}
          <Logo variant="navbar" id="navbar-logo" onClick={() => setMenuOpen(false)} />

          {/* Center: Desktop Nav */}
          <nav className={styles.desktopNav} aria-label="Main Navigation">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`${styles.navLink} ${
                  location.pathname === item.path ? styles.active : ''
                }`}
                id={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right: Desktop CTA & Mobile Hamburger */}
          <div className={styles.actions}>
            <button
              type="button"
              onClick={() => openEstimator()}
              className={styles.ctaButton}
              id="navbar-cta"
            >
              <span>GET AN ESTIMATE</span>
              <ArrowRight size={16} className={styles.ctaIcon} />
            </button>

            <button
              className={styles.hamburger}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              id="navbar-hamburger"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={menuOpen ? 'x' : 'menu'}
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.2 }}
                >
                  {menuOpen ? <X size={22} /> : <Menu size={22} />}
                </motion.span>
              </AnimatePresence>
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className={styles.mobileMenu}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            id="mobile-menu"
            role="dialog"
            aria-label="Mobile navigation"
          >
            <nav className={styles.mobileNav} aria-label="Mobile Navigation">
              {[{ label: 'Home', path: '/' }, ...navItems].map((item, i) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={item.path}
                    className={`${styles.mobileNavLink} ${
                      location.pathname === item.path ? styles.mobileActive : ''
                    }`}
                    id={`mobile-nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>

            <motion.div
              className={styles.mobileCtaWrapper}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  openEstimator();
                }}
                className={styles.mobileCta}
                id="mobile-cta"
              >
                <span>GET AN ESTIMATE</span>
                <ArrowRight size={20} className={styles.mobileCtaIcon} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
